import { memo } from 'react';
import { ReactComponent as OpenEye } from 'assets/svg/eye-open.svg';
import { ReactComponent as OpenEyeHide } from 'assets/svg/eye-close.svg';
import RoundedButton from 'components/RoundedButtons';
import Input from 'components/Input';
import { useDispatch, useSelector } from 'react-redux';
import ErrorComponent from 'components/ErrorComponent';
import { setDialogState } from '../../../reduxStore/reducers/dialogSlice';
import {
  forgot_password,
  label_text,
  login,
  login_text2,
  login_text3,
  name,
  placeholder,
  signin,
  signup,
} from '../constants/text';
import { useNavigate } from 'react-router-dom';
import DropDownField from 'components/DropDownField';
import { COUNTRIES } from 'constants/country.constants';

const LoginChildren = memo((props) => {
  const {
    formik,
    isLogin,
    passwordHide,
    setPasswordHide,
    dropdownKey,
    retryCountdown = 0,
    formatRetryTime,
  } = props;
  const { handleChange, errors, handleBlur, values, touched } = formik;
  const { dialogOpenState } = useSelector((state) => state.dialogOpenState);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const changeModalText = () => {
    if (isLogin) {
      dispatch(
        setDialogState({
          ...dialogOpenState,
          logIn: false,
          register: true,
        })
      );
    } else {
      dispatch(
        setDialogState({
          ...dialogOpenState,
          logIn: true,
          register: false,
        })
      );
    }
  };

  const handleCountryChange = (selectedOption) => {
    formik.setFieldValue(
      name.country,
      selectedOption ? selectedOption.value : ''
    );
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      {!isLogin && (
        <>
          <Input
            name={name.first_name}
            inputClassName={'font-semibold'}
            placeholder={placeholder.first_name}
            labelText={label_text.first_name}
            onChange={handleChange}
            isError={errors.first_name}
            onBlur={handleBlur}
            value={values.first_name}
          />
          {errors.first_name && touched.first_name ? (
            <ErrorComponent text={errors.first_name} />
          ) : null}

          <Input
            name={name.last_name}
            inputClassName={'font-semibold'}
            placeholder={placeholder.last_name}
            labelText={label_text.last_name}
            onChange={handleChange}
            isError={errors.last_name}
            onBlur={handleBlur}
            value={values.last_name}
            mainTagClassname='md:mt-6 mt-4'
          />
          {errors.last_name && touched.last_name ? (
            <ErrorComponent text={errors.last_name} />
          ) : null}
        </>
      )}

      <Input
        name={name.email}
        inputClassName={'font-semibold'}
        placeholder={placeholder.email}
        labelText={label_text.email}
        onChange={handleChange}
        isError={errors.email}
        onBlur={handleBlur}
        value={values.email}
        mainTagClassname={!isLogin ? 'md:mt-6 mt-4' : ''}
      />
      {errors.email && touched.email ? (
        <ErrorComponent text={errors.email} />
      ) : null}

      <Input
        isError={errors.password}
        maxLength={15}
        mainTagClassname='md:mt-6 mt-4'
        inputClassName={'font-semibold'}
        type={passwordHide ? 'password' : 'text'}
        placeholder={placeholder.password}
        labelText={label_text.password}
        onChange={handleChange}
        onBlur={handleBlur}
        value={values.password}
        name={name.password}
        EndIcon={
          passwordHide ? (
            <OpenEyeHide onClick={() => setPasswordHide(!passwordHide)} />
          ) : (
            <OpenEye
              className='stroke-primary'
              onClick={() => setPasswordHide(!passwordHide)}
            />
          )
        }
      />
      {errors.password && touched.password ? (
        <ErrorComponent text={errors.password} />
      ) : null}

      {!isLogin && (
        <div className='pt-6'>
          <DropDownField
            placeholder={placeholder.country}
            label={label_text.country}
            onChange={handleCountryChange}
            isSearchable={true}
            onBlur={handleBlur}
            isError={errors.country}
            options={COUNTRIES}
            isLabelSticky={false}
            mainTagClassname='mt-4 mb-4'
            key={dropdownKey}
          />
          {errors.country && touched.country ? (
            <ErrorComponent text={errors.country} />
          ) : null}
        </div>
      )}

      {isLogin ? (
        <div className='flex mt-4 mb-6'>
          <p
            className='text-primary cursor-pointer ml-auto text-[14px] md:text-[16px] font-semibold'
            onClick={() =>
              dispatch(
                setDialogState({
                  ...dialogOpenState,
                  logIn: false,
                  resetPassword: true,
                })
              )
            }
          >
            {forgot_password}
          </p>
        </div>
      ) : (
        <p className='font-[400] lg:mt-8 md:mt-6 mt-4 mb-6 text-[12px] md:text-[14px] leading-[19.1px] text-gray-2'>
          {login_text2}{' '}
          <span
            className='text-[14px] font-semibold underline cursor-pointer decoration-1'
            onClick={() => {
              dispatch(
                setDialogState({
                  logIn: false,
                  register: false,
                  resetPassword: false,
                  otp: false,
                  newPassword: false,
                })
              );
              navigate('/termsofuse');
            }}
          >
            Terms of Use
          </span>{' '}
          and{' '}
          <span
            className='text-[14px] font-semibold underline cursor-pointer decoration-1'
            onClick={() => {
              dispatch(
                setDialogState({
                  logIn: false,
                  register: false,
                  resetPassword: false,
                  otp: false,
                  newPassword: false,
                })
              );
              navigate('/privacypolicy');
            }}
          >
            Privacy Policy
          </span>{' '}
          . {login_text3}
        </p>
      )}

      {isLogin && retryCountdown > 0 ? (
        <p className='text-rose text-[13px] md:text-[14px] text-center mb-3'>
          Too many attempts. Try again in {formatRetryTime(retryCountdown)}
        </p>
      ) : null}

      <div className='flex w-full'>
        <RoundedButton
          isWidthFull={true}
          className={'w-full font-semibold md:text-[16px] justify-center'}
          text={isLogin ? login : signup}
          type='submit'
          disabled={isLogin && retryCountdown > 0}
        />
      </div>

      <p className='text-gray-3 text-[13px] md:text-[16px] mt-3 text-center font-semibold'>
        {isLogin ? "Don't have an account?" : 'Already have an account?'}
        <span
          className='text-primary cursor-pointer'
          onClick={() => {
            changeModalText();
          }}
        >
          {' '}
          {isLogin ? signup : signin}
        </span>
      </p>
    </form>
  
  );
});

export default LoginChildren;
